/**
 * Curated starter decks — one-click loadouts so players can jump straight in
 * without hand-building. All are tuned to the default 30-point budget and
 * pursue a distinct strategy.
 */
import { DEPLOYABLE_PIECES, buildCost, type DeckBuild } from "../core";

export interface StarterDeck {
  readonly name: string;
  readonly blurb: string;
  readonly build: DeckBuild;
}

export const STARTER_DECKS: readonly StarterDeck[] = [
  {
    name: "Cavalry",
    // Plan: fast minors swarm and fork the Prince, then the Queen mates the King.
    blurb: "Fast minors — knights fork toward the Prince, lances stab forward, and the Queen finishes the King.",
    // 5 + 4 + 6 + 6 + 9 = 30
    build: { pawn: 5, lance: 2, knight: 2, bishop: 2, queen: 1 },
  },
  {
    name: "Siege",
    // Plan: open files with rooks, plant pieces deep via the Outpost to corner the Prince.
    blurb: "Heavy and long-range — rooks and a Queen pry lanes open while the Outpost plants pieces deep to trap the Prince.",
    // 5 + 10 + 3 + 9 + 3 = 30
    build: { pawn: 5, rook: 2, bishop: 1, queen: 1, outpost: 1 },
  },
  {
    name: "Aggro",
    // Plan: flood the board and rush the Prince; the Captain's free move lands the tempo blow.
    blurb: "Rush the Prince — Farmers and Lances flood forward and the Captain's free move every turn lands the killing tempo.",
    // 6 + 6 + 6 + 6 + 6 = 30 (Captain now costs 6)
    build: { pawn: 6, farmer: 6, lance: 3, outpost: 2, captain: 1 },
  },
  {
    name: "Tactician",
    // Plan: Dash/Sidestep spring a minor onto the Prince a move early; Recall/Resurrection sustain.
    blurb: "Tempo tricks — Dash and Sidestep spring a knight onto the Prince a move early; Recall and Resurrection keep the pressure coming.",
    // 6 + 6 + 6 + 3 + 3 + 2 + 4 = 30
    build: { pawn: 6, knight: 2, bishop: 2, sidestep: 3, dash: 3, recall: 1, resurrection: 1 },
  },
  {
    name: "Terrain",
    // Plan: wall the board with terrain, funnel the King, break through to the Prince.
    blurb: "Reshape the board — a Mound, Hills, and Walls funnel the enemy King while your minors break through to the Prince.",
    // 5 + 6 + 6 + 3 + 4 + 4 + 2 = 30
    build: { pawn: 5, knight: 2, bishop: 2, hill: 1, valley: 1, ridge: 1, recall: 1 },
  },
];

/**
 * Draft a deck that fits `budget` by greedily buying affordable pieces at
 * random. Used to give the computer opponent its own loadout. A handful of
 * pawns are always seeded first so the AI has cheap, plentiful cards to deploy.
 */
export function randomDeck(budget: number, random: () => number = Math.random): DeckBuild {
  const build: DeckBuild = {};
  const add = (type: (typeof DEPLOYABLE_PIECES)[number]["id"], n = 1) => {
    build[type] = (build[type] ?? 0) + n;
  };

  // Seed two pawns (if affordable) for early deployment, then fill the rest.
  const pawnCost = DEPLOYABLE_PIECES.find((d) => d.id === "pawn")!.cost;
  for (let i = 0; i < 2 && buildCost(build) + pawnCost <= budget; i++) add("pawn");

  // Greedily buy random affordable pieces until nothing else fits, respecting
  // each piece's per-deck copy limit.
  for (let guard = 0; guard < 200; guard++) {
    const remaining = budget - buildCost(build);
    const affordable = DEPLOYABLE_PIECES.filter(
      (d) =>
        d.cost <= remaining &&
        (d.maxCopies === undefined || (build[d.id] ?? 0) < d.maxCopies),
    );
    if (affordable.length === 0) break;
    const pick = affordable[Math.floor(random() * affordable.length)]!;
    add(pick.id);
  }

  return build;
}
